/**
 * Cấu hình cổng gọi service `production-planning-service` (tên cũ: `production-performance`).
 *
 * Service đi qua CÙNG gateway với mọi API khác, dưới tiền tố `/api/production-planning` — không
 * phải một host riêng. Vì vậy đây là đường dẫn TƯƠNG ĐỐI, cùng origin với trang đang mở:
 *
 *   - không phụ thuộc hostname của từng môi trường, staging và production dùng chung một giá trị;
 *   - không phát sinh preflight CORS, nên gateway không cần whitelist thêm `Authorization` và
 *     `X-Refresh-Token` cho origin của factory webapp;
 *   - khớp đúng cách `REACT_APP_HOST_API=/api` và `REACT_APP_HOST_API_ORDER=/api/order` đang làm.
 *
 * ⚠️ Gateway KHÔNG cắt tiền tố (ingress dùng `pathType: Prefix`, không có rewrite) — câu trước ở đây
 *    ghi ngược lại và đó là lý do staging 404 toàn bộ hôm 16/09/2026.
 *
 *    Endpoint phía service vẫn nằm ở gốc (`/config/standard-time/*`, `/planning/capacity/*`,
 *    `/control/output/*`, `/performance-records/*`, `/summary/*`, `/heartbeat/*`). Chỗ khớp hai bên
 *    lại với nhau nằm ở CHÍNH SERVICE: nó tự cắt `/api/production-planning` khỏi `req.url`
 *    (`src/shared/utils/strip-prefix.util.ts`), đúng cách exporter/packing/fulfillment-service làm
 *    bằng `app.use('/api/<tên>', routes)`. Vì vậy baseURL ở đây mang tiền tố là đúng.
 *
 * Muốn trỏ riêng service này sang chỗ khác (chạy BE ở máy mình, hoặc chọc thẳng vào staging khi
 * dev server chưa có proxy) thì đặt `REACT_APP_PRODUCTION_PERFORMANCE_URL` trong `.env.local` —
 * file đó đã được `.gitignore` bỏ qua, và mọi API còn lại vẫn đi theo `configs/server.js`.
 */

import {timeoutFromEnv} from './apiTimeout'

export const PRODUCTION_PERFORMANCE_URL =
    process.env.REACT_APP_PRODUCTION_PERFORMANCE_URL || '/api/production-planning'

/**
 * Lớp ĐÈ riêng cho cổng này, `null` khi không đặt.
 *
 * ⚠️ `null` chứ KHÔNG phải 30000. Trả về con số mặc định ở đây là nó ĐÈ luôn lên
 *    `REACT_APP_API_TIMEOUT_MS` chung — biến chung sẽ không bao giờ có tác dụng với cổng này, mà
 *    không có dấu hiệu nào. Thứ tự ưu tiên và phần còn lại ở `configs/apiTimeout.js`.
 *
 * Chỉ đặt khi muốn RIÊNG service này chờ lâu hơn phần còn lại của app; thường thì nâng biến chung
 * là đủ.
 */
export const PRODUCTION_PERFORMANCE_TIMEOUT_MS = timeoutFromEnv(
    process.env.REACT_APP_PRODUCTION_PERFORMANCE_TIMEOUT_MS,
    null
)

/**
 * Trần chờ RIÊNG cho hai endpoint báo cáo sản lượng M18 — `/control/output/report` và
 * `/control/output/report/stages`.
 *
 * ⚠️ Chỉ hai endpoint đó, KHÔNG phải cả cổng production-planning và càng không phải cả app. Mọi
 *    API khác của service này (định mức, năng lực, lưới giờ làm, chi tiết sản lượng) vẫn chạy
 *    dưới trần chung 30 s. Nới trần cho toàn cổng là che luôn những endpoint lẽ ra phải nhanh —
 *    lúc chúng chậm đi thì không ai biết.
 *
 * **Vì sao hai endpoint này cần riêng.** Chúng đọc log thao tác ở mức TỪNG LƯỢT QUÉT rồi tính ra
 * các chỉ số về DANH TÍNH sản phẩm (bao nhiêu sản phẩm khác nhau, bao nhiêu lần làm lại, lần nào
 * là lần đầu). Danh tính không cộng dồn được, nên không có cách nào tổng hợp trước — đã đo ba
 * phương án và đều thua: gom tại Mongo chậm hơn 2–2,5×, rollup theo ngày làm sai `redoCount`
 * −81,6%, bảng trung gian giữ barcode chỉ giảm 12,5% số dòng.
 *
 * Số đo trên dữ liệu prod (30/10/2026, mirror 12 nhân — prod chỉ **1 vCPU** nên thực tế còn chậm
 * hơn):
 *
 *   | khoảng lọc        | dòng log  | thời gian          |
 *   |-------------------|-----------|--------------------|
 *   | Hôm nay (mặc định)|    26.946 | 0,62 s             |
 *   | 7 ngày            |   156.516 | 2,55 s             |
 *   | 30 ngày (preset)  |   507.864 | 8,56 s             |
 *   | 59 ngày (kéo tay) |   850.332 | 13,9 – 21,7 s      |
 *   | 59 ngày × 3 người |         — | **28,1 s**         |
 *
 * Trần khoảng lọc phía service là 92 ngày, nên vẫn còn chỗ chậm hơn nữa.
 *
 * ⚠️ **Trần ngắn KHÔNG làm giảm tải, chỉ làm mất kết quả.** Đo trực tiếp bằng `currentOp`: ngắt
 *    client ở giây thứ 2 thì Mongo vẫn chạy tiếp tới giây 3,4 — trình duyệt bỏ cuộc không dừng
 *    được việc ở server. Nên huỷ ở giây 30 là vứt đi phần việc đã làm xong 90%, rồi người dùng
 *    bấm lại và server làm lại từ đầu. Nới trần ở đây vì vậy làm GIẢM tải chứ không tăng.
 *
 * ⚠️ KHÔNG dùng `timeout: 0` như hai hàm nhập file. Ở đó người dùng chủ động chọn file và chờ;
 *    còn đây là màn hình mở ra là gọi, nên phải có điểm dừng — `0` nghĩa là chờ tới tận TCP
 *    timeout của hệ điều hành mà không báo gì (xem `configs/apiTimeout.js`).
 *
 * 90 giây là con số tạm: đủ cho khoảng rộng nhất cộng vài người xem trùng giờ, và đủ ngắn để
 * người dùng còn nhận được lỗi thay vì ngồi nhìn vòng xoay mãi. Đây là BĂNG DÁN, không phải bản
 * vá — hướng xử lý thật nằm ở phía service (cache câu trả lời, gộp request trùng, trần số lượt
 * nặng chạy song song, `maxTimeMS`). Giảm lại ngay khi những thứ đó lên.
 */
export const OUTPUT_REPORT_TIMEOUT_MS = timeoutFromEnv(
    process.env.REACT_APP_OUTPUT_REPORT_TIMEOUT_MS,
    90000
)
